import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Loader2, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { AuthShell } from "@/components/layouts";
import { Field } from "@/components/common";
import { useAuth } from "@/context/AuthContext";
import { useBranding } from "@/context/BrandingContext";
import { errMsg } from "@/lib/api";

export default function Login() {
  const { login } = useAuth();
  const { config, branding } = useBranding();
  const nav = useNavigate();
  const [f, setF] = useState({ email: "", password: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      const u = await login(f.email, f.password);
      toast.success("Welcome back");
      nav(u.role === "admin" ? "/admin" : "/dashboard");
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthShell>
      <p className="eyebrow mb-3">Sign in</p>
      <h1 className="text-4xl font-semibold">Welcome to {branding.website_name}</h1>
      <p className="text-sm text-muted-foreground mt-2">Access your creator dashboard.</p>
      {config?.setup_required && (
        <div className="mt-6 rounded-xl border border-primary/30 bg-primary/5 p-4 text-sm" data-testid="setup-required-banner">
          No admin exists yet. <Link to="/signup" className="text-primary font-medium underline">Complete initial admin setup</Link>.
        </div>
      )}
      <form onSubmit={submit} className="mt-8 space-y-4" data-testid="login-form">
        <Field label="Email"><Input type="email" required value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className="h-12 rounded-xl bg-card" data-testid="login-email-input" /></Field>
        <Field label="Password"><Input type="password" required value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} className="h-12 rounded-xl bg-card" data-testid="login-password-input" /></Field>
        {error && <p className="text-sm text-destructive" data-testid="login-error">{error}</p>}
        <div className="flex justify-end"><Link to="/forgot-password" className="text-xs text-primary hover:underline" data-testid="forgot-password-link">Forgot password?</Link></div>
        <Button type="submit" disabled={busy} className="w-full h-12 rounded-full font-semibold" data-testid="login-submit-button">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Sign in <ArrowRight className="h-4 w-4 ml-2" /></>}
        </Button>
      </form>
      <p className="text-sm text-muted-foreground mt-8">New creator? <Link to="/signup" className="text-primary hover:underline" data-testid="goto-signup-link">Apply to join</Link></p>
    </AuthShell>
  );
}
